import { Refresh } from "@mui/icons-material";
import { Alert, Card, CircularProgress, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import { FinalReportSummaryCardFragment } from "./fragment.generated";

type Props = {
    visit: FinalReportSummaryCardFragment;
    onRegenerate?: () => void;
    regenerating: boolean;
    notUpdated: boolean;
    error?: { message: string };
    beingGenerated: boolean;
};

export default function FinalReportSummaryCard({ visit, onRegenerate, regenerating, notUpdated, error, beingGenerated }: Props) {
    const { t } = useTranslation("project");

    const summary = visit.summaryText ?? "";

    return (
        <Card component={Stack} padding={2} gap={2} variant="outlined">
            <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography variant="h5">{t("projectReviewVisitV2.finalReportRoute.finalReportSummaryCard.title")}</Typography>

                {onRegenerate && (
                    <Tooltip title={t("projectReviewVisitV2.finalReportRoute.finalReportSummaryCard.regenerate")}>
                        <span>
                            <IconButton
                                aria-label={t("projectReviewVisitV2.finalReportRoute.finalReportSummaryCard.regenerate")}
                                disabled={regenerating}
                                onClick={onRegenerate}
                            >
                                {regenerating ? <CircularProgress size={20} /> : <Refresh />}
                            </IconButton>
                        </span>
                    </Tooltip>
                )}
            </Stack>

            {error && <Alert severity="error">{error.message}</Alert>}

            {notUpdated && (
                <Alert severity="warning">{t("projectReviewVisitV2.finalReportRoute.finalReportSummaryCard.notUpdated")}</Alert>
            )}

            {summary.length > 0 ? (
                <SummaryText>{summary}</SummaryText>
            ) : beingGenerated ? (
                <Stack direction="row" alignItems="center" gap={1}>
                    <CircularProgress size={16} />
                    <Typography color="text.secondary">
                        {t("projectReviewVisitV2.finalReportRoute.finalReportSummaryCard.beingGenerated")}
                    </Typography>
                </Stack>
            ) : (
                <Typography color="text.secondary">
                    {t("projectReviewVisitV2.finalReportRoute.finalReportSummaryCard.notAvailable")}
                </Typography>
            )}
        </Card>
    );
}

function SummaryText({ children }: { children: string }) {
    return (
        <Typography
            variant="body1"
            sx={theme => ({
                whiteSpace: "pre-wrap",
                backgroundColor: theme.palette.action.hover,
                borderRadius: 1,
                padding: 2,
            })}
        >
            {children}
        </Typography>
    );
}
